import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

// Types
export interface TeamMember {
  _id: string;
  name: string;
  role: string;
  description: string;
  image: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  order?: number;
  isActive?: boolean;
}

export interface Project {
  _id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  client?: string;
  location?: string;
  completionDate?: string;
  technologies?: string[];
  url?: string;
  isFeatured?: boolean;
  order?: number;
}

interface DataContextType {
  team: TeamMember[];
  projects: Project[];
  contactMessages: any[];
  isLoading: boolean;
  error: string | null;
  addTeamMember: (member: Omit<TeamMember, '_id'>) => Promise<void>;
  updateTeamMember: (id: string, member: Partial<TeamMember>) => Promise<void>;
  deleteTeamMember: (id: string) => Promise<void>;
  addProject: (project: Omit<Project, '_id'>) => Promise<void>;
  updateProject: (id: string, project: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  fetchTeam: () => Promise<void>;
  fetchProjects: () => Promise<void>;
  submitContact: (data: any) => Promise<{ success: boolean; message: string }>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [contactMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch team members
  const fetchTeam = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/team');
      setTeam(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch team members');
      console.error('Error fetching team:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchTeam();
    fetchProjects();
  }, []);

  // Team CRUD operations
  const addTeamMember = async (member: Omit<TeamMember, '_id'>) => {
    try {
      const response = await api.post('/team', member);
      setTeam(prev => [...prev, response.data]);
      return Promise.resolve();
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to add team member');
    }
  };

  const updateTeamMember = async (id: string, member: Partial<TeamMember>) => {
    try {
      const response = await api.put(`/team/${id}`, member);
      setTeam(prev => prev.map(m => m._id === id ? response.data : m));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update team member');
    }
  };

  const deleteTeamMember = async (id: string) => {
    try {
      await api.delete(`/team/${id}`);
      setTeam(prev => prev.filter(m => m._id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete team member');
    }
  };

  // Project CRUD operations
  const addProject = async (project: Omit<Project, '_id'>) => {
    try {
      const response = await api.post('/projects', project);
      setProjects(prev => [...prev, response.data]);
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to add project');
    }
  };

  const updateProject = async (id: string, project: Partial<Project>) => {
    try {
      const response = await api.put(`/projects/${id}`, project);
      setProjects(prev => prev.map(p => p._id === id ? response.data : p));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update project');
    }
  };

  const deleteProject = async (id: string) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects(prev => prev.filter(p => p._id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete project');
    }
  };

  // Contact form submission
  const submitContact = async (data: any) => {
    try {
      await api.post('/contact', data);
      return { success: true, message: 'Message sent successfully!' };
    } catch (err: any) {
      return { 
        success: false, 
        message: err.response?.data?.error || 'Failed to send message' 
      };
    }
  };

  return (
    <DataContext.Provider value={{
      team,
      projects,
      contactMessages,
      isLoading,
      error,
      addTeamMember,
      updateTeamMember,
      deleteTeamMember,
      addProject,
      updateProject,
      deleteProject,
      fetchTeam,
      fetchProjects,
      submitContact,
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

